/**
 * Shared constants, formatting and decision helpers for the Sales Name Check
 * page. Pure functions only — no React, no HTTP.
 */

export const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

export const hasAcceptedExtension = (fileName) => {
  const lower = String(fileName || '').toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

export const GROUPS = [
  { key: 'matched', label: 'Matched' },
  { key: 'confident', label: 'Confident' },
  { key: 'decision', label: 'Needs a decision' },
  { key: 'nomatch', label: 'No match' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatNumber = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
export const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
export const priceText = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const ymd = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
};

/** 'YYYY-MM-DD' (read as written, never shifted by time zone) or an ISO timestamp → '3 Sep'. */
export const shortDate = (value) => {
  if (!value) return '';
  const text = String(value);
  const p = ymd(text);
  if (p && text.length <= 10) return `${p.d} ${MONTHS[p.m - 1]}`;
  const d = new Date(text);
  if (Number.isNaN(d.getTime())) return text;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

/** '2026-09-01','2026-09-30' → '1–30 Sep'; across months → '28 Aug – 30 Sep'. */
export const formatRange = (from, to) => {
  const a = ymd(from);
  const b = ymd(to);
  if (!a && !b) return 'dates unknown';
  if (!a || !b) return shortDate(from || to);
  if (a.y === b.y && a.m === b.m) {
    return a.d === b.d ? `${a.d} ${MONTHS[a.m - 1]}` : `${a.d}–${b.d} ${MONTHS[a.m - 1]}`;
  }
  return `${a.d} ${MONTHS[a.m - 1]} – ${b.d} ${MONTHS[b.m - 1]}`;
};

/** Inclusive day count between two 'YYYY-MM-DD' dates; null when either is missing. */
export const daysBetween = (from, to) => {
  const a = ymd(from);
  const b = ymd(to);
  if (!a || !b) return null;
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86400000) + 1;
};

/** Days spanned by all active uploads (earliest dateFrom to latest dateTo). */
export const periodDays = (uploads) => {
  const froms = (uploads || []).map((u) => u.dateFrom).filter(Boolean).sort();
  const tos = (uploads || []).map((u) => u.dateTo).filter(Boolean).sort();
  if (froms.length === 0 || tos.length === 0) return null;
  return daysBetween(froms[0], tos[tos.length - 1]);
};

/** The "stay Under Review until confirmed" note: only while an added dish is still unconfirmed (W-6). */
export const showUnderReviewNote = (dishes) => Array.isArray(dishes)
  && dishes.some((d) => d && d.recipeStatus !== 'confirmed');

export const DATE_SOURCE_LABELS = {
  column: 'dates from the file',
  header: 'dates from the report header',
  file_name: 'dates from the file name',
  owner: 'month you chose',
};

/** 0 → 'A', 25 → 'Z', 26 → 'AA'. */
export const columnLetter = (index) => {
  let n = Number(index);
  let s = '';
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
};

/** 'YYYY-MM' of the month before today — the usual month an owner uploads. */
export const previousMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ── Decisions ────────────────────────────────────────────────────────────────

export const SKIP_REASONS = [
  { value: 'charge', label: 'Charge (packing, delivery…)' },
  { value: 'resold_item', label: 'Resold item (bottled water, soft drink…)' },
  { value: 'not_food', label: 'Not food' },
  { value: 'other', label: 'Other' },
];

/** 'other' travels as 'other: <text>' (spec §8.1 "other (with text)"); null when incomplete. */
export const buildSkipReason = (reason, text) => {
  if (!reason) return null;
  if (reason !== 'other') return reason;
  const t = String(text || '').trim();
  return t ? `other: ${t}` : null;
};

export const skipReasonLabel = (skipReason) => {
  if (!skipReason) return '';
  const s = String(skipReason);
  if (s.startsWith('other:')) return s.slice(6).trim();
  const known = SKIP_REASONS.find((r) => r.value === s);
  return known ? known.label.replace(/\s*\(.*\)$/, '').toLowerCase() : s;
};

/** Every PUT /names/:id body carries the whole decision; unset fields are explicit. */
export const blankDecision = () => ({
  action: null,
  targetMenuItemId: null,
  priceSplit: null,
  usePosName: false,
  newPrice: null,
  addName: null,
  addPrice: null,
  skipReason: null,
});

/** The row's saved decision as a PUT body (so a change to one field keeps the rest). */
export const decisionBody = (row) => {
  const d = (row && row.decision) || {};
  return {
    action: d.action ?? null,
    targetMenuItemId: d.targetMenuItemId ?? null,
    priceSplit: Array.isArray(d.priceSplit)
      ? d.priceSplit.map((p) => ({ price: Number(p.price), menuItemId: Number(p.menuItemId) }))
      : null,
    usePosName: d.usePosName === true,
    newPrice: d.newPrice ?? null,
    addName: d.addName ?? null,
    addPrice: d.addPrice ?? null,
    skipReason: d.skipReason ?? null,
  };
};

/** Matched rows need no decision: remembered/exact already resolved them (spec §7). */
export const isUndecided = (row) => row.group !== 'matched' && !(row.decision && row.decision.action);

export const isConflict = (row) => Boolean(row.result && row.result.code === 'conflict');

/** The action shown as active: the saved decision, else what a matched row already is. */
export const effectiveAction = (row) => {
  if (row.decision && row.decision.action) return row.decision.action;
  if (row.group === 'matched') return row.flags && row.flags.rememberedSkip ? 'skip' : 'link';
  return null;
};

/** Resume target (spec §12): the first undecided (or conflicting) row of the first group that has one. */
export const firstOpenRow = (rows) => {
  for (const g of GROUPS) {
    const hit = (rows || []).find((r) => r.group === g.key && (isUndecided(r) || isConflict(r)));
    if (hit) return { group: g.key, nameId: hit.nameId };
  }
  return null;
};

export const sameName = (a, b) =>
  String(a || '').trim().replace(/\s+/g, ' ').toLowerCase() === String(b || '').trim().replace(/\s+/g, ' ').toLowerCase();
